import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, ScrollView, Animated } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';
import LandingStyleHeader from '../components/LandingStyleHeader';
import { auth } from '../firebaseConfig';

export default function DashboardScreen({ navigation }) {
  const { colors, glass, isDark, gradients } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [greeting, setGreeting] = useState('');
  const [hoveredCard, setHoveredCard] = useState(null);
  const [placeholderText, setPlaceholderText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  const [isFocused, setIsFocused] = useState(false);
  const [isSearchHovered, setIsSearchHovered] = useState(false);

  // Animation values
  const heroOpacity = useRef(new Animated.Value(0)).current;
  const heroTranslateY = useRef(new Animated.Value(30)).current;
  const cardAnimations = useRef(Array(5).fill(0).map(() => ({
    opacity: new Animated.Value(0),
    translateY: new Animated.Value(30),
    scale: new Animated.Value(0.95)
  }))).current;

  const placeholderExamples = [
    'Paste job posts to evaluate opportunities...',
    'Create proposal for web redesign...',
    'Organize: call client, review mockups...',
    'Case study for e-commerce platform...',
    'Generate proposal for mobile app...',
  ];

  useEffect(() => {
    document.title = 'Flomodo - Dashboard';
    
    // Set greeting based on time of day
    const hour = new Date().getHours();
    if (hour < 12) setGreeting('Good morning');
    else if (hour < 18) setGreeting('Good afternoon');
    else setGreeting('Good evening');

    // Trigger animations
    Animated.parallel([
      Animated.timing(heroOpacity, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }),
      Animated.timing(heroTranslateY, {
        toValue: 0,
        duration: 600,
        useNativeDriver: true,
      }),
    ]).start();

    // Stagger card animations
    cardAnimations.forEach((anim, index) => {
      Animated.parallel([
        Animated.timing(anim.opacity, {
          toValue: 1,
          duration: 500,
          delay: 200 + (index * 100),
          useNativeDriver: true,
        }),
        Animated.timing(anim.translateY, {
          toValue: 0,
          duration: 500,
          delay: 200 + (index * 100),
          useNativeDriver: true,
        }),
        Animated.timing(anim.scale, {
          toValue: 1,
          duration: 500,
          delay: 200 + (index * 100),
          useNativeDriver: true,
        }),
      ]).start();
    });
  }, []);

  // Typing animation effect for placeholder
  useEffect(() => {
    if (isFocused) return;

    let currentExampleIndex = 0;
    let currentCharIndex = 0;
    let typingTimeout;
    let pauseTimeout;
    const typingSpeed = 80;
    const pauseDuration = 2000;
    const deletingSpeed = 40;

    const typeNextChar = () => {
      const currentExample = placeholderExamples[currentExampleIndex];
      
      if (isTyping) {
        if (currentCharIndex < currentExample.length) {
          setPlaceholderText(currentExample.substring(0, currentCharIndex + 1));
          currentCharIndex++;
          typingTimeout = setTimeout(typeNextChar, typingSpeed);
        } else {
          pauseTimeout = setTimeout(() => {
            setIsTyping(false);
            typeNextChar();
          }, pauseDuration);
        }
      } else {
        if (currentCharIndex > 0) {
          currentCharIndex--;
          setPlaceholderText(currentExample.substring(0, currentCharIndex));
          typingTimeout = setTimeout(typeNextChar, deletingSpeed);
        } else {
          currentExampleIndex = (currentExampleIndex + 1) % placeholderExamples.length;
          setIsTyping(true);
          setTimeout(typeNextChar, 300);
        }
      }
    };

    typeNextChar();

    return () => {
      clearTimeout(typingTimeout);
      clearTimeout(pauseTimeout);
    };
  }, [isFocused]);

  const userName = auth.currentUser?.email?.split('@')[0] || 'there';

  const quickLinks = [
    {
      id: 'proposal',
      title: 'Create Proposal',
      description: 'AI-powered proposal writing',
      icon: '✨',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      action: () => navigation.navigate('wizard'),
      keywords: ['proposal', 'create', 'generate', 'write']
    },
    {
      id: 'jobselector',
      title: 'Job Selector',
      description: 'Evaluate freelance opportunities',
      icon: '🎯',
      gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
      action: () => navigation.navigate('jobselector'),
      keywords: ['job', 'evaluate', 'upwork', 'contra', 'freelance', 'opportunity']
    },
    {
      id: 'tasks',
      title: 'Task Manager',
      description: 'Organize your workflow',
      icon: '📋',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      action: () => navigation.navigate('tasks'),
      keywords: ['task', 'todo', 'organize', 'workflow']
    },
    {
      id: 'casestudy',
      title: 'Case Studies',
      description: 'Create portfolio content',
      icon: '📊',
      gradient: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
      action: () => navigation.navigate('casestudy'),
      keywords: ['case study', 'portfolio', 'project']
    },
    {
      id: 'templates',
      title: 'Templates & Examples',
      description: 'Manage your content library',
      icon: '📚',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      action: () => navigation.navigate('settings'),
      keywords: ['template', 'example', 'library', 'manage']
    },
  ];

  const handleSearch = () => {
    if (!searchQuery.trim()) return;
    
    const query = searchQuery.toLowerCase();
    const content = searchQuery.trim();
    
    // Check if content looks like job posts (multiple lines, job titles, etc.)
    const looksLikeJobs = content.split('\n').length > 3 || 
                         content.match(/\$([\d,]+)/g) || 
                         content.includes('budget') ||
                         content.includes('hiring') ||
                         content.includes('looking for');

    if (looksLikeJobs) {
      // Auto-detect job posts and go to Job Selector
      sessionStorage.setItem('pendingJobBatch', content);
      navigation.navigate('jobselector');
      setSearchQuery('');
      return;
    }

    // Check keywords for each component
    for (const link of quickLinks) {
      const matchesKeyword = link.keywords.some(keyword => query.includes(keyword));
      if (matchesKeyword) {
        // Store content based on component
        if (link.id === 'proposal') {
          const descMatch = content.match(/(?:proposal|create|generate)\s+(?:for|about)?\s*(.+)/i);
          const projectDesc = descMatch ? descMatch[1] : content;
          sessionStorage.setItem('pendingProjectDescription', projectDesc);
        } else if (link.id === 'tasks') {
          const taskMatch = content.match(/(?:task|todo|organize)\s*:?\s*(.+)/i);
          const taskContent = taskMatch ? taskMatch[1] : content;
          sessionStorage.setItem('pendingTaskInput', taskContent);
        } else if (link.id === 'casestudy') {
          const caseMatch = content.match(/(?:case study|casestudy)\s+(?:for|about)?\s*(.+)/i);
          const caseContent = caseMatch ? caseMatch[1] : content;
          sessionStorage.setItem('pendingCaseStudyName', caseContent);
        } else if (link.id === 'jobselector') {
          sessionStorage.setItem('pendingJobBatch', content);
        }
        
        link.action();
        setSearchQuery('');
        return;
      }
    }

    // Smart default: if it looks like substantial content, go to proposal wizard
    if (content.length > 20) {
      sessionStorage.setItem('pendingProjectDescription', content);
      navigation.navigate('wizard');
    } else {
      navigation.navigate('wizard');
    }
    
    setSearchQuery('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <View style={[styles.container, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]}>
      <LandingStyleHeader navigation={navigation} currentScreen="dashboard" />
      
      <ScrollView 
        style={[styles.content, { backgroundImage: isDark ? gradients.background : 'none', backgroundColor: colors.primary }]} 
        contentContainerStyle={styles.contentContainer}
        onScroll={(e) => {
          const scrollY = e.nativeEvent.contentOffset.y;
          window.lastKnownScrollY = scrollY;
          window.dispatchEvent(new Event('scroll'));
        }}
        scrollEventThrottle={16}
      >
        {/* Hero Section with Animation */}
        <Animated.View 
          style={[
            styles.heroSection,
            {
              opacity: heroOpacity,
              transform: [{ translateY: heroTranslateY }]
            }
          ]}
        >
          <Text style={[styles.greeting, { color: colors.text }]}>{greeting}, {userName}!</Text>
          <Text style={[styles.subtitle, { color: colors.textSecondary }]}>
            Paste anything below to get started, or choose a tool
          </Text>
          
          {/* Enhanced Smart Search */}
          <View 
            style={[
              styles.searchContainer,
              isSearchHovered && styles.searchContainerHovered,
              isFocused && styles.searchContainerFocused
            ]}
            onMouseEnter={() => setIsSearchHovered(true)}
            onMouseLeave={() => setIsSearchHovered(false)}
          >
            <Text style={styles.searchIcon}>🔍</Text>
            <TextInput
              style={[styles.searchInput, { color: colors.text }]}
              placeholder={placeholderText}
              placeholderTextColor={colors.textSecondary}
              value={searchQuery}
              onChangeText={setSearchQuery}
              onSubmitEditing={handleSearch}
              onKeyPress={handleKeyPress}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              multiline
            />
            {searchQuery && (
              <TouchableOpacity onPress={handleSearch} style={styles.searchButton}>
                <Text style={styles.searchButtonText}>Go →</Text>
              </TouchableOpacity>
            )}
          </View>

          <Text style={[styles.hint, { color: colors.textSecondary }]}>
            💡 Tip: Paste job posts, project descriptions, or tasks - we'll figure out where to put them
          </Text>
        </Animated.View>

        {/* Grid Layout for Quick Actions */}
        <View style={styles.gridSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Quick Actions</Text>
          
          <View style={styles.grid}>
            {quickLinks.map((link, index) => (
              <Animated.View
                key={link.id}
                style={[
                  styles.gridItemWrapper,
                  {
                    opacity: cardAnimations[index].opacity,
                    transform: [
                      { translateY: cardAnimations[index].translateY },
                      { scale: cardAnimations[index].scale }
                    ]
                  }
                ]}
              >
                <TouchableOpacity
                  style={[
                    styles.gridItem,
                    { 
                      backgroundImage: hoveredCard === link.id ? link.gradient : 'none',
                      backgroundColor: hoveredCard === link.id ? 'transparent' : colors.card,
                      borderColor: hoveredCard === link.id ? 'transparent' : colors.border,
                    }
                  ]}
                  onPress={link.action}
                  onMouseEnter={() => setHoveredCard(link.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <View style={styles.cardIcon}>
                    <Text style={styles.iconText}>{link.icon}</Text>
                  </View>
                  <Text style={[
                    styles.cardTitle, 
                    { color: hoveredCard === link.id ? '#fff' : colors.text }
                  ]}>
                    {link.title}
                  </Text>
                  <Text style={[
                    styles.cardDescription, 
                    { color: hoveredCard === link.id ? 'rgba(255,255,255,0.9)' : colors.textSecondary }
                  ]}>
                    {link.description}
                  </Text>
                </TouchableOpacity>
              </Animated.View>
            ))}
          </View>
        </View>

        {/* Getting Started Section */}
        <View style={[styles.tipsSection, { backgroundColor: colors.card, borderColor: colors.border }]}>
          <Text style={[styles.tipsTitle, { color: colors.text }]}>✨ Getting Started</Text>
          <View style={styles.tipsList}>
            <View style={styles.tipItem}>
              <Text style={styles.tipEmoji}>🎯</Text>
              <View style={styles.tipContent}>
                <Text style={[styles.tipTitle, { color: colors.text }]}>Evaluate Jobs</Text>
                <Text style={[styles.tipText, { color: colors.textSecondary }]}>
                  Paste job posts from Upwork/Contra into the search above
                </Text>
              </View>
            </View>
            <View style={styles.tipItem}>
              <Text style={styles.tipEmoji}>✨</Text>
              <View style={styles.tipContent}>
                <Text style={[styles.tipTitle, { color: colors.text }]}>Generate Proposals</Text>
                <Text style={[styles.tipText, { color: colors.textSecondary }]}>
                  Paste project descriptions to create winning proposals
                </Text>
              </View>
            </View>
            <View style={styles.tipItem}>
              <Text style={styles.tipEmoji}>📋</Text>
              <View style={styles.tipContent}>
                <Text style={[styles.tipTitle, { color: colors.text }]}>Organize Tasks</Text>
                <Text style={[styles.tipText, { color: colors.textSecondary }]}>
                  Convert conversations into structured task lists
                </Text>
              </View>
            </View>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
  },
  contentContainer: {
    paddingBottom: 80,
  },
  heroSection: {
    paddingHorizontal: 32,
    paddingTop: 40,
    paddingBottom: 48,
    alignItems: 'center',
  },
  greeting: {
    fontSize: 40,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  subtitle: {
    fontSize: 18,
    marginBottom: 32,
    textAlign: 'center',
  },
  searchContainer: {
    width: '100%',
    maxWidth: 800,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    transition: 'all 0.3s ease',
  },
  searchContainerHovered: {
    borderColor: 'rgba(162, 89, 255, 0.5)',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  searchContainerFocused: {
    borderColor: '#A259FF',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    shadowColor: '#A259FF',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.3,
    shadowRadius: 20,
  },
  searchIcon: {
    fontSize: 20,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    minHeight: 24,
    maxHeight: 150,
    outlineStyle: 'none',
  },
  searchButton: {
    backgroundColor: '#A259FF',
    paddingHorizontal: 20,
    paddingVertical: 8,
    borderRadius: 8,
    marginLeft: 12,
  },
  searchButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  hint: {
    fontSize: 14,
    marginTop: 16,
    textAlign: 'center',
    opacity: 0.7,
  },
  gridSection: {
    paddingHorizontal: 32,
    paddingBottom: 40,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 24,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'flex-start',
  },
  gridItemWrapper: {
    width: 'calc(33.333% - 14px)',
    minWidth: 280,
  },
  gridItem: {
    padding: 24,
    borderRadius: 16,
    borderWidth: 1,
    minHeight: 180,
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  cardIcon: {
    width: 56,
    height: 56,
    borderRadius: 12,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  iconText: {
    fontSize: 28,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  cardDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  tipsSection: {
    marginHorizontal: 32,
    marginBottom: 40,
    padding: 32,
    borderRadius: 16,
    borderWidth: 1,
  },
  tipsTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 24,
  },
  tipsList: {
    gap: 20,
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 16,
  },
  tipEmoji: {
    fontSize: 24,
    marginTop: 2,
  },
  tipContent: {
    flex: 1,
  },
  tipTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 4,
  },
  tipText: {
    fontSize: 14,
    lineHeight: 20,
  },
});
